import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../Analitos.scss';
import DataGrid, { Column, Button as Butt, Editing, Popup as Pop, Lookup, RequiredRule, Scrolling } from 'devextreme-react/data-grid';
import TextArea from 'devextreme-react/text-area';
import { useApiConfiguration } from '../../../api/api.configuration';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { TypeRules, SysIAnalitos } from '../../../types/types.analitos';
import { TranslationsBuilder, parseTranslations, hasEdgeSpaces } from './TranslationsBuilder';
import { FormulaBuilder } from './FormulaBuilder';

export const ValidationRules = ({ IdAnalito, analytes = [] }: { IdAnalito: number; analytes?: SysIAnalitos[] }) => {
  const { getValidationRules, newValidationRules, setValidationRules, deleteValidationRules } = useApiConfiguration();
  const dataRef = useRef<DataGrid>(null);

  const TypesRules = Object.keys(TypeRules)
    .map((key) => {
      if (!isNaN(Number(key))) {
        return null;
      }

      const value = TypeRules[key as keyof typeof TypeRules];
      return {
        id: value,
        text: key,
      };
    })
    .filter(Boolean);

  const customData = useMemo(() => {
    return new CustomStore({
      key: 'Rga_Id',
      load() {
        return getValidationRules(IdAnalito)
          .then((res) => ({
            data: res,
            totalCount: res.totalCount,
            summary: res.summary,
            groupCount: res.groupCount,
          }))
          .catch(() => {
            throw new Error('Error de carga de datos');
          });
      },
      insert: async (values) => {
        const newValues = {
          ...values,
          Rga_An_Id: IdAnalito,
        };
        return newValidationRules({ Rules: newValues })
          .then((res) => {
            if (res === 105) {
              dataRef.current?.instance.refresh();
            }
          })
          .catch(() => {
            throw new Error('Error insertando la regla de validación');
          });
      },
      update: (key, values) => {
        return new Promise((resolve, reject) => {
          dataRef.current?.instance
            .byKey(key)
            .then((originalRecord) => {
              // Mezclar el registro original con los valores modificados
              const fullRecord = { ...originalRecord, ...values };

              setValidationRules({ Rules: fullRecord, IdRegla: key })
                .then((res) => {
                  if (res === 107) {
                    dataRef.current?.instance.refresh();
                  }
                  resolve(res);
                })
                .catch((error) => {
                  reject(new Error('Error actualizando la regla de validación'));
                });
            })
            .catch(reject);
        });
      },
      remove: (key) => {
        return deleteValidationRules({ IdRegla: key, IdAnalito: IdAnalito }).then((res) => {
          if (res === 107) {
            dataRef.current?.instance.refresh();
          }
        });
      },
    });
  }, [IdAnalito]);

  const customDataSrc = new DataSource({
    store: customData,
    reshapeOnPush: true,
  });

  const onEditorPreparing = (e) => {
    if ((e.dataField === 'Rga_Requerido' || e.dataField === 'Rga_Estado') && e.parentType === 'dataRow') {
      e.editorName = 'dxCheckBox';
    }
  };

  const isTranslations = (tipo: number | string | null | undefined) => Number(tipo) === TypeRules.ResultadosTraducciones;
  const isFormula = (tipo: number | string | null | undefined) =>
    Number(tipo) === TypeRules.ResultadosPropios || Number(tipo) === TypeRules.ResultadosOtrosAnalitos;

  // Al cambiar el TIPO también tocamos Rga_Formula para forzar el re-render del editor
  // de FÓRMULA (DevExtreme solo repinta el editor del campo que cambió).
  const onTipoValueChanged = (newData, value, currentRowData) => {
    newData.Rga_Tipo = value;
    const current = currentRowData?.Rga_Formula ?? '';
    const looksLikeTranslations = /Result\s*:/.test(current);
    if (isTranslations(value)) {
      newData.Rga_Formula = looksLikeTranslations ? current : '[]';
    } else if (isFormula(value)) {
      // Si venía de traducciones, limpiar el JSON para no arrastrarlo a la fórmula.
      newData.Rga_Formula = looksLikeTranslations ? '' : current;
    } else {
      newData.Rga_Formula = current;
    }
  };

  const FormulaEditor = ({ data }) => {
    const tipo = data.data?.Rga_Tipo;
    if (isTranslations(tipo)) {
      return <TranslationsBuilder value={data.value} onValueChange={(val) => data.setValue(val)} />;
    }
    if (isFormula(tipo)) {
      return (
        <FormulaBuilder
          value={data.value}
          onValueChange={(val) => data.setValue(val)}
          analytes={analytes}
          showComparators={Number(tipo) === TypeRules.ResultadosPropios}
        />
      );
    }
    return <TextArea defaultValue={data.value ?? ''} autoResizeEnabled minHeight={70} onValueChanged={(e) => data.setValue(e.value)} />;
  };

  const FormulaCell = ({ data }) => {
    const tipo = data.data?.Rga_Tipo;
    if (isTranslations(tipo)) {
      const pairs = parseTranslations(data.value);
      if (!pairs.length) return <span className='formula-empty'>Sin traducciones</span>;
      return (
        <div className='formula-chips'>
          {pairs.map((p, i) => (
            <span className='formula-chip' key={i}>
              <b>{p.result}</b>
              {hasEdgeSpaces(p.result) && (
                <span className='formula-chip-space' title={`Tiene espacios al inicio o al final: "${p.result}"`}>
                  ␣
                </span>
              )}
              <span className='formula-chip-arrow'>→</span>
              {p.translation}
            </span>
          ))}
        </div>
      );
    }
    if (isFormula(tipo)) {
      const parts: string[] = (data.value ?? '').split(/(\[[^\]]+\])/g);
      return (
        <div className='formula-expr'>
          {parts.map((part: string, i: number) =>
            part.startsWith('[') && part.endsWith(']') ? (
              <span className='formula-var' key={i}>
                {part.slice(1, -1)}
              </span>
            ) : (
              <span key={i}>{part}</span>
            )
          )}
        </div>
      );
    }
    return <div className='formula-text'>{data.value}</div>;
  };

  return (
    <div>
      <DataGrid id='1' dataSource={customDataSrc} ref={dataRef} width={'100%'} columnAutoWidth={false} wordWrapEnabled={true} onEditorPreparing={onEditorPreparing}>
        <Scrolling columnRenderingMode='standard' useNative={true} scrollByContent={true} showScrollbar='always' />
        <Editing mode='form' allowUpdating={true} allowAdding={true} allowDeleting={true} selectTextOnEditStart={true} />
        <Column allowGrouping={false} caption='TIPO' visible={true} allowEditing={true} dataField='Rga_Tipo' alignment='center' setCellValue={onTipoValueChanged}>
          <Lookup dataSource={TypesRules} valueExpr='id' displayExpr='text' />
          <RequiredRule />
        </Column>
        <Column allowGrouping={false} caption='REGLA' visible={true} allowEditing={true} dataField='Rga_Nombre' alignment='center'>
          <RequiredRule />
        </Column>
        <Column
          allowGrouping={false}
          caption='FÓRMULA'
          visible={true}
          allowEditing={true}
          dataField='Rga_Formula'
          alignment='left'
          minWidth={280}
          editCellComponent={FormulaEditor}
          cellComponent={FormulaCell}
        />
        <Column allowGrouping={false} caption='ORDEN' visible={true} allowEditing={true} dataField='Rga_Orden' alignment='center'>
          <RequiredRule />
        </Column>
        <Column allowGrouping={false} caption='ESTADO' visible={true} allowEditing={true} dataField='Rga_Estado' alignment='center'>
          <RequiredRule />
        </Column>
        <Column allowGrouping={false} caption='REQUERIDO' visible={true} allowEditing={true} dataField='Rga_Requerido' alignment='center'>
          <RequiredRule />
        </Column>
        <Column type='buttons' caption={'ACCIÓN'} width={200}>
          <Butt name='edit' />
          <Butt name='delete' />
        </Column>
      </DataGrid>
    </div>
  );
};
